import {
  AssetClass,
  DepositStatus,
  FeeType,
  OrderType,
  TransactionDirection,
  TransactionType,
  WithdrawReason,
  WithdrawStatus,
} from "@prisma/client";
import Decimal from "decimal.js";
import { z } from "zod";

const DecimalSchema = z.coerce
  .number()
  .nonnegative()
  .transform((v) => new Decimal(v));

export const FeeTransactionSchema = z.object({
  assetId: z.string().cuid(),
  cost: DecimalSchema,
  feeType: z.nativeEnum(FeeType),
});

export const BaseTransactionSchema = z.object({
  portfolioId: z.string().cuid(),
  userId: z.string().cuid(),
  assetId: z.string().cuid(),
  amount: DecimalSchema,
  price: DecimalSchema,
  cost: DecimalSchema,
  type: z.nativeEnum(TransactionType),
  direction: z
    .nativeEnum(TransactionDirection)
    .default(TransactionDirection.IN),
  isValid: z.boolean().default(true),
  note: z.string().optional(),
  meta: z.record(z.any()).nullable(),
  id: z.string().cuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),

  fee: FeeTransactionSchema.nullish(),
});

export const TradeTransactionSchema = z.object({
  ...BaseTransactionSchema.shape,
  type: z.literal(TransactionType.TRADE),
  orderType: z.nativeEnum(OrderType).default(OrderType.MARKET),
  quoteAssetId: z.string().cuid(),
  quoteAmount: DecimalSchema,
  quotePrice: DecimalSchema,
});

export const SwapTransactionSchema = z.object({
  ...BaseTransactionSchema.shape,
  type: z.literal(TransactionType.SWAP),
  toAssetId: z.string().cuid(),
  toAmount: DecimalSchema,
  toPrice: DecimalSchema,
  toCost: DecimalSchema,
  slippage: DecimalSchema.optional(),
  provider: z.string().optional(),
  routingPath: z.array(z.string()).optional(),
});

export const DepositTransactionSchema = z.object({
  ...BaseTransactionSchema.shape,
  type: z.literal(TransactionType.DEPOSIT),
  direction: z.literal(TransactionDirection.IN),
  fromAddress: z.string().optional(),
  txHash: z.string().optional(),
  network: z.string().optional(),
  status: z.nativeEnum(DepositStatus).default(DepositStatus.PENDING),
});

export const WithdrawalTransactionSchema = z.object({
  ...BaseTransactionSchema.shape,
  type: z.literal(TransactionType.WITHDRAW),
  direction: z.literal(TransactionDirection.OUT),
  toAddress: z.string().optional(),
  txHash: z.string().optional(),
  network: z.string().optional(),
  status: z.nativeEnum(WithdrawStatus).default(WithdrawStatus.PENDING),
  reason: z.nativeEnum(WithdrawReason).nullish(),
});

export const FillTransactionSchema = z.object({
  ...BaseTransactionSchema.shape,
  type: z.literal(TransactionType.FILL),
  price: DecimalSchema,
  amount: DecimalSchema,
  cost: DecimalSchema,
});

export const TransactionSchema = z.union([
  TradeTransactionSchema,
  SwapTransactionSchema,
  DepositTransactionSchema,
  WithdrawalTransactionSchema,
  FeeTransactionSchema,
  FillTransactionSchema,
]);

const BaseAssetSchema = z.object({
  id: z.string().cuid().optional(),
  symbol: z.string(),
  name: z.string(),
  assetClass: z.nativeEnum(AssetClass).default(AssetClass.CRYPTO),
  thirdPartyIds: z
    .object({
      coingecko: z.string(),
    })
    .nullish(),
  image: z
    .object({
      small: z.string().url(),
      large: z.string().url(),
      thumb: z.string().url(),
    })
    .optional(),
});

export const TransactionDbSchema = BaseTransactionSchema.extend({
  fee: FeeTransactionSchema.extend({
    asset: BaseAssetSchema,
  })
    .nullish()
    .transform((v) => {
      if (!v) return undefined;
      const { asset, ...rest } = v;

      return {
        ...rest,
        asset,
        symbol: asset.symbol,
      };
    }),
  trade: TradeTransactionSchema.pick({
    orderType: true,
    quoteAssetId: true,
    quoteAmount: true,
    quotePrice: true,
  })
    .extend({
      quoteAsset: BaseAssetSchema,
    })
    .nullish()
    .transform((v) => v ?? undefined),
  swap: SwapTransactionSchema.pick({
    toAssetId: true,
    toAmount: true,
    toPrice: true,
    toCost: true,
  })
    .nullish()
    .transform((v) => v ?? undefined),
  deposit: DepositTransactionSchema.pick({
    status: true,
  })
    .nullish()
    .transform((v) => v ?? undefined),
  withdrawal: WithdrawalTransactionSchema.pick({
    status: true,
    reason: true,
  })
    .nullish()
    .transform((v) => v ?? undefined),
  fill: FillTransactionSchema.pick({
    amount: true,
    price: true,
    cost: true,
  })
    .nullish()
    .transform((v) => v ?? undefined),

  meta: z
    .object({
      keyAction: z
        .object({
          type: z.string(),
        })
        .nullish(),
    })
    .nullish(),
});
export type PwTransaction = z.infer<typeof TransactionDbSchema>;

//#region Input Schemas

export const BaseTransactionInputSchema = BaseTransactionSchema.omit({
  id: true,
  updatedAt: true,
});

export const TradeTransactionInputSchema = TradeTransactionSchema.omit({
  id: true,
  updatedAt: true,
});

export const SwapTransactionInputSchema = SwapTransactionSchema.omit({
  id: true,
  updatedAt: true,
});

export const DepositTransactionInputSchema = DepositTransactionSchema.omit({
  id: true,
  updatedAt: true,
});

export const WithdrawalTransactionInputSchema =
  WithdrawalTransactionSchema.omit({
    id: true,
    updatedAt: true,
  });

export const FillTransactionInputSchema = FillTransactionSchema.omit({
  id: true,
  updatedAt: true,
});

export const TransactionInputSchema = z.discriminatedUnion("type", [
  TradeTransactionInputSchema,
  SwapTransactionInputSchema,
  DepositTransactionInputSchema,
  WithdrawalTransactionInputSchema,
  FillTransactionInputSchema,
]);

export type PwTransactionInput = z.infer<typeof TransactionInputSchema>;

export type InferTransactionInputSchema<T extends TransactionType> =
  T extends "TRADE"
    ? z.infer<typeof TradeTransactionInputSchema>
    : T extends "SWAP"
      ? z.infer<typeof SwapTransactionInputSchema>
      : T extends "DEPOSIT"
        ? z.infer<typeof DepositTransactionInputSchema>
        : T extends "WITHDRAW"
          ? z.infer<typeof WithdrawalTransactionInputSchema>
          : T extends "FILL"
            ? z.infer<typeof FillTransactionInputSchema>
            : never;

//#endregion

export function getTxTypeAndDirectionFromInputTx(
  transactionType: TransactionType,
) {
  let direction: TransactionDirection = TransactionDirection.IN;
  let type: TransactionType = TransactionType.TRADE;

  switch (true) {
    case transactionType === TransactionType.BUY:
      direction = TransactionDirection.IN;
      break;
    case transactionType === TransactionType.SELL:
      direction = TransactionDirection.OUT;
      break;
    case transactionType === TransactionType.DEPOSIT:
      direction = TransactionDirection.IN;
      type = TransactionType.DEPOSIT;
      break;
    case transactionType === TransactionType.WITHDRAW:
      direction = TransactionDirection.OUT;
      type = TransactionType.WITHDRAW;
      break;
    case transactionType === TransactionType.SWAP:
      direction = TransactionDirection.OUT;
      type = TransactionType.SWAP;
      break;
    case transactionType === TransactionType.CLOSE:
      direction = TransactionDirection.OUT;
      type = TransactionType.WITHDRAW;
      break;
  }

  return { direction, type };
}
